import React from "react"

/**
 * Small visual badge that mirrors the colored "file icon" used by Files.jsx
 * (the white card with a Lucide icon on top and a colored extension label
 * underneath, e.g. JSON in orange or PDF in red). Extracted so non-Files
 * surfaces — currently the corpus DocumentsTab list — can render the same
 * file-format affordance without depending on the full Files component
 * (which carries upload/run-output behavior we don't want here).
 *
 * The mime-type / extension → color mapping is intentionally a copy of the
 * one inside Files.jsx::getFileConfig so the visuals stay identical. If the
 * two ever diverge meaningfully, hoist the table into a shared module and
 * have both call sites import it.
 */

// Resolve a {icon, bgColor, label, fontSize} record for a given extension or
// mime type. Accepts strings like "pdf", ".pdf", "application/pdf".
function getFileConfig(typeOrMime) {
  const raw = String(typeOrMime || "").toLowerCase().trim()
  let extension = raw.replace(/^\./, "")
  if (extension.includes("/")) {
    const mimeMap = {
      "application/pdf": "pdf",
      "application/json": "json",
      "application/zip": "zip",
      "application/vnd.ms-excel": "xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
      "text/markdown": "md",
      "text/plain": "txt",
      "text/csv": "csv",
      "text/html": "html",
      "application/x-research-info-systems": "ris",
      "application/x-bibtex": "bib",
      "application/x-nbib": "nbib",
    }
    extension = mimeMap[extension] || extension.split("/").pop() || ""
  }

  switch (extension) {
    case "pdf":
      return { icon: "file-pen", color: "#D32F2F", bgColor: "rgb(211, 47, 47)", label: "PDF", fontSize: "7.2px" }
    case "json":
      return { icon: "file-code", color: "#FF9800", bgColor: "rgb(255, 152, 0)", label: "JSON", fontSize: "7.2px" }
    case "txt":
      return { icon: "file-text", color: "#2196F3", bgColor: "rgb(33, 150, 243)", label: "TXT", fontSize: "9px" }
    case "md":
    case "markdown":
      return { icon: "file-text", color: "#673AB7", bgColor: "rgb(103, 58, 183)", label: "MD", fontSize: "9px" }
    case "csv":
      return { icon: "file-text", color: "#2563EB", bgColor: "rgb(37, 99, 235)", label: "CSV", fontSize: "7.2px" }
    case "xlsx":
    case "xls":
      return { icon: "file", color: "#16A34A", bgColor: "rgb(22, 163, 74)", label: extension === "xls" ? "XLS" : "XLSX", fontSize: "6.75px" }
    case "html":
    case "htm":
      return { icon: "file-code", color: "#EA580C", bgColor: "rgb(234, 88, 12)", label: "HTML", fontSize: "6.75px" }
    case "xml":
      return { icon: "file-code", color: "#7C3AED", bgColor: "rgb(124, 58, 237)", label: "XML", fontSize: "7.2px" }
    case "nbib":
      return { icon: "file-text", color: "#0D9488", bgColor: "rgb(13, 148, 136)", label: "NBIB", fontSize: "6.75px" }
    case "ris":
      return { icon: "file-text", color: "#059669", bgColor: "rgb(5, 150, 105)", label: "RIS", fontSize: "7.2px" }
    case "bib":
      return { icon: "file-code", color: "#7C3AED", bgColor: "rgb(124, 58, 237)", label: "BIB", fontSize: "7.2px" }
    case "zip":
      return { icon: "archive", color: "#795548", bgColor: "rgb(121, 85, 72)", label: "ZIP", fontSize: "9px" }
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
      return { icon: "file", color: "#0EA5E9", bgColor: "rgb(14, 165, 233)", label: extension.toUpperCase(), fontSize: "7.2px" }
    default:
      return { icon: "file", color: "#8C929D", bgColor: "rgb(140, 146, 157)", label: (extension || "FILE").slice(0, 4).toUpperCase(), fontSize: "6.75px" }
  }
}

function Icon({ name, color }) {
  const props = {
    xmlns: "http://www.w3.org/2000/svg",
    width: "18",
    height: "18",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: `lucide lucide-${name} bg-transparent`,
  }
  switch (name) {
    case "file-text":
      return (
        <svg {...props}>
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
          <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
          <path d="M10 9H8"></path>
          <path d="M16 13H8"></path>
          <path d="M16 17H8"></path>
        </svg>
      )
    case "file-pen":
      return (
        <svg {...props}>
          <path d="M12.5 22H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v9.5"></path>
          <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
          <path d="M13.378 15.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"></path>
        </svg>
      )
    case "file-code":
      return (
        <svg {...props}>
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
          <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
          <path d="m10 9-2 2 2 2"></path>
          <path d="m14 9 2 2-2 2"></path>
        </svg>
      )
    case "archive":
      return (
        <svg {...props}>
          <rect width="20" height="5" x="2" y="3" rx="1"></rect>
          <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"></path>
          <path d="M10 12h4"></path>
        </svg>
      )
    case "file":
    default:
      return (
        <svg {...props}>
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
          <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
        </svg>
      )
  }
}

export default function FileFormatBadge({ type, size = "md", className = "" }) {
  const config = getFileConfig(type)
  const dims = size === "sm"
    ? { width: 24, top: 18, label: 10, padding: 2, font: config.fontSize }
    : { width: 30, top: 24, label: 12, padding: 3, font: config.fontSize }
  return (
    <div
      className={`flex shrink-0 flex-col overflow-hidden shadow-md transition-all duration-200 hover:shadow-lg ${className}`}
      style={{ width: `${dims.width}px`, height: "auto", borderRadius: "6px" }}
    >
      <div
        className="flex w-full items-center justify-center bg-white dark:bg-gray-900"
        style={{ height: `${dims.top}px`, padding: `${dims.padding}px`, backgroundColor: "rgb(222, 222, 222)" }}
      >
        <Icon name={config.icon} color={config.color} />
      </div>
      <div
        className="flex w-full items-center justify-center"
        style={{ height: `${dims.label}px`, backgroundColor: config.bgColor }}
      >
        <span
          className="w-full text-center font-medium"
          style={{ color: "rgb(232, 232, 232)", fontSize: dims.font }}
        >
          {config.label}
        </span>
      </div>
    </div>
  )
}
